import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../utils/axiosInstance";
import { Alert, Badge, Card, Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// Predefined insurance schemes — must match app/src/page/Ingo.jsx.
const INSURANCE_TYPES = [
  "Mituweli", "RSSB", "RAMA", "MMI", "Radiant", "Britam", "Prime", "Ibindi",
];
const YEAR_NOW = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => YEAR_NOW - i);

const theme = {
  bg: "#f1f5f9",
  card: "#ffffff",
  text: "#334155",
  textBright: "#0f172a",
  textMuted: "#64748b",
  primary: "#1e3a5f",
  success: "#16a34a",
  danger: "#dc2626",
  border: "#e2e8f0",
};

// Insurance name shown to the user (custom text when "Ibindi").
const insuranceLabel = (h) =>
  (h?.ubwishingizi === "Ibindi" && h?.ubwishingizi_bwihariye)
    ? h.ubwishingizi_bwihariye
    : (h?.ubwishingizi || "—");

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("rw-RW");
};

const CSS = `
  .ubw-root { font-size: 15px; }
  .ubw-title { font-size: clamp(1.1rem, 4vw, 1.45rem); font-weight: 800; color: #0f172a; margin: 0; display: flex; align-items: center; gap: .5rem; }

  /* summary tiles */
  .ubw-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  @media (min-width: 576px){ .ubw-stats { grid-template-columns: repeat(3, 1fr); } }
  @media (min-width: 992px){ .ubw-stats { grid-template-columns: repeat(6, 1fr); } }
  .ubw-stat { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid var(--c, #1e3a5f); border-radius: 12px; padding: .7rem .85rem; }
  .ubw-stat-v { font-size: 1.6rem; font-weight: 800; line-height: 1.1; color: var(--c, #1e3a5f); }
  .ubw-stat-l { font-size: .82rem; color: #64748b; margin-top: 2px; line-height: 1.25; }

  /* section card */
  .ubw-section { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; margin-bottom: 1.1rem; overflow: hidden; }
  .ubw-section-head { display: flex; align-items: center; gap: .5rem; padding: .8rem 1rem; background: #f8fafc; border-bottom: 1px solid #e2e8f0; font-size: 1rem; font-weight: 800; color: #0f172a; }
  .ubw-section-body { padding: .75rem; }

  /* mobile: one card per household */
  .ubw-cards { display: grid; gap: 10px; }
  .ubw-rec { border: 1px solid #e2e8f0; border-radius: 12px; padding: .8rem .9rem; background: #fff; }
  .ubw-rec-top { display: flex; justify-content: space-between; align-items: flex-start; gap: .6rem; }
  .ubw-rec-name { font-size: 1.02rem; font-weight: 700; color: #0f172a; line-height: 1.3; word-break: break-word; }
  .ubw-rec-members { font-size: .82rem; color: #64748b; margin-top: 2px; }
  .ubw-rec-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .55rem .9rem; margin-top: .7rem; }
  .ubw-f-l { font-size: .68rem; text-transform: uppercase; letter-spacing: .04em; color: #94a3b8; font-weight: 800; }
  .ubw-f-v { font-size: .92rem; color: #1e293b; font-weight: 500; word-break: break-word; }
  .ubw-rec-act { margin-top: .75rem; padding-top: .7rem; border-top: 1px dashed #e2e8f0; }
  .ubw-btn { width: 100%; min-height: 42px; border-radius: 10px; border: 1px solid; font-weight: 700; font-size: .9rem; display: inline-flex; align-items: center; justify-content: center; gap: .4rem; cursor: pointer; }
  .ubw-btn.on  { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
  .ubw-btn.off { background: #1e3a5f; border-color: #1e3a5f; color: #fff; }
  .ubw-btn:disabled { opacity: .55; cursor: default; }

  /* desktop: real table */
  .ubw-table-wrap { overflow-x: auto; }
  .ubw-table { width: 100%; border-collapse: collapse; font-size: .92rem; }
  .ubw-table th { font-size: .72rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 800; padding: 10px 12px; border-bottom: 2px solid #e2e8f0; text-align: left; white-space: nowrap; }
  .ubw-table td { padding: 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #1e293b; }
  .ubw-table tbody tr:hover td { background: #f8fafc; }
  .ubw-table .u-name { font-weight: 700; color: #0f172a; }
  .ubw-empty { text-align: center; color: #94a3b8; padding: 1.5rem .5rem; font-size: .95rem; }

  .ubw-badge { display: inline-block; padding: .28rem .6rem; border-radius: 999px; font-size: .78rem; font-weight: 800; }
  .ubw-badge.paid   { background: #dcfce7; color: #15803d; }
  .ubw-badge.unpaid { background: #fee2e2; color: #b91c1c; }
  .ubw-badge.na     { background: #f1f5f9; color: #94a3b8; }

  @media (max-width: 991px){ .ubw-desktop { display: none; } }
  @media (min-width: 992px){ .ubw-mobileonly { display: none; } }
`;

const MutuelleBadge = ({ h }) => {
  if (h.ubwishingizi !== "Mituweli") return <span className="ubw-badge na">—</span>;
  return h.mutuelle_yishyuwe
    ? <span className="ubw-badge paid">Yishyuwe</span>
    : <span className="ubw-badge unpaid">Ntabwo yishyuwe</span>;
};

const Ubwishingizi = ({ isUmudugudu = false }) => {
  const [year, setYear] = useState(YEAR_NOW);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [paidFilter, setPaidFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const notify = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: "", variant: "" }), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const base = isUmudugudu ? "/umudugudu/module-data/ingo" : "/ingo";
      const res = await axios.get(`${base}?year=${year}`);
      const data = Array.isArray(res.data)
        ? res.data
        : (res.data?.data ?? res.data?.ingo ?? []);
      setRows(data);
    } catch (err) {
      notify("Habaye ikosa mu gukura amakuru y'ubwishingizi", "danger");
    } finally {
      setLoading(false);
    }
  }, [isUmudugudu, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markMutuelle = async (household, paid) => {
    if (isUmudugudu) return;
    setBusyId(household.urugo_id);
    try {
      if (paid) {
        await axios.post(`/ingo/${household.urugo_id}/mutuelle`, {
          year,
          wanditse_izina: localStorage.getItem("fullname") || "",
        });
        notify(`Mituweli ${year} yashyizweho ko yishyuwe kuri ${household.umukuru_w_urugo}.`);
      } else {
        await axios.delete(`/ingo/${household.urugo_id}/mutuelle/${year}`);
        notify(`Mituweli ${year} yakuweho kuri ${household.umukuru_w_urugo}.`);
      }
      await fetchData();
    } catch (err) {
      notify(err.response?.data?.error || "Ntibyakunze guhindura Mituweli", "danger");
    } finally {
      setBusyId(null);
    }
  };

  // Apply search + type + paid filters.
  const filtered = useMemo(() => {
    let res = [...rows];
    const term = search.trim().toLowerCase();
    if (term) {
      res = res.filter(
        (h) =>
          h.umukuru_w_urugo?.toLowerCase().includes(term) ||
          h.umudugudu?.toLowerCase().includes(term) ||
          h.izina_ry_isibo?.toLowerCase().includes(term) ||
          insuranceLabel(h).toLowerCase().includes(term)
      );
    }
    if (typeFilter !== "all")
      res = res.filter((h) => (h.ubwishingizi || "") === typeFilter);
    if (paidFilter !== "all")
      res = res.filter((h) =>
        paidFilter === "yishyuwe" ? !!h.mutuelle_yishyuwe : !h.mutuelle_yishyuwe
      );
    return res;
  }, [rows, search, typeFilter, paidFilter]);

  // Split into the three organised groups the spec asks for.
  const groups = useMemo(() => {
    const mituweli = [];
    const other = [];
    const ibindi = [];
    filtered.forEach((h) => {
      if (h.ubwishingizi === "Mituweli") mituweli.push(h);
      else if (h.ubwishingizi === "Ibindi") ibindi.push(h);
      else if (h.ubwishingizi) other.push(h);
    });
    return { mituweli, other, ibindi };
  }, [filtered]);

  const stats = useMemo(() => {
    const mituweli = rows.filter((h) => h.ubwishingizi === "Mituweli");
    return {
      total: rows.length,
      mituweli: mituweli.length,
      paid: mituweli.filter((h) => h.mutuelle_yishyuwe).length,
      notPaid: mituweli.filter((h) => !h.mutuelle_yishyuwe).length,
      other: rows.filter((h) => h.ubwishingizi && h.ubwishingizi !== "Mituweli" && h.ubwishingizi !== "Ibindi").length,
      ibindi: rows.filter((h) => h.ubwishingizi === "Ibindi").length,
    };
  }, [rows]);

  const statCards = [
    { label: "Ingo zose", value: stats.total, color: theme.primary },
    { label: "Zifite Mituweli", value: stats.mituweli, color: "#0ea5e9" },
    { label: `Zishyuye ${year}`, value: stats.paid, color: theme.success },
    { label: `Zitarishyura ${year}`, value: stats.notPaid, color: theme.danger },
    { label: "Ubundi bwishingizi", value: stats.other, color: "#7c3aed" },
    { label: "Ibindi", value: stats.ibindi, color: "#b45309" },
  ];

  // ---- mobile: card per household ----
  const RecordCard = ({ h, showToggle }) => {
    const isMit = h.ubwishingizi === "Mituweli";
    const paid = !!h.mutuelle_yishyuwe;
    return (
      <div className="ubw-rec">
        <div className="ubw-rec-top">
          <div>
            <div className="ubw-rec-name">{h.umukuru_w_urugo || "—"}</div>
            <div className="ubw-rec-members">
              <i className="bi bi-people me-1" />{h.umubare_w_abagize || 0} mu rugo
            </div>
          </div>
          <MutuelleBadge h={h} />
        </div>

        <div className="ubw-rec-grid">
          <div>
            <div className="ubw-f-l">Umudugudu</div>
            <div className="ubw-f-v">{h.umudugudu || "—"}</div>
          </div>
          <div>
            <div className="ubw-f-l">Isibo</div>
            <div className="ubw-f-v">{h.izina_ry_isibo || "—"}</div>
          </div>
          <div>
            <div className="ubw-f-l">Ubwishingizi</div>
            <div className="ubw-f-v">{insuranceLabel(h)}</div>
          </div>
          {isMit && paid && (
            <>
              <div>
                <div className="ubw-f-l">Umwaka</div>
                <div className="ubw-f-v">{h.mutuelle_year || year}</div>
              </div>
              <div>
                <div className="ubw-f-l">Itariki yishyuriweho</div>
                <div className="ubw-f-v">{fmtDate(h.mutuelle_itariki)}</div>
              </div>
              <div>
                <div className="ubw-f-l">Wanditse</div>
                <div className="ubw-f-v">{h.mutuelle_wanditse || "—"}</div>
              </div>
            </>
          )}
        </div>

        {showToggle && !isUmudugudu && isMit && (
          <div className="ubw-rec-act">
            <button
              type="button"
              className={`ubw-btn ${paid ? "on" : "off"}`}
              disabled={busyId === h.urugo_id}
              onClick={() => markMutuelle(h, !paid)}
            >
              {busyId === h.urugo_id ? (
                <Spinner animation="border" size="sm" />
              ) : paid ? (
                <><i className="bi bi-x-circle" /> Kuraho ko yishyuwe</>
              ) : (
                <><i className="bi bi-check-circle" /> Shyiraho ko yishyuwe</>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ---- desktop: table ----
  const RecordTable = ({ list, showToggle }) => {
    const actionCol = showToggle && !isUmudugudu;
    return (
      <div className="ubw-table-wrap">
        <table className="ubw-table">
          <thead>
            <tr>
              <th>Urugo</th>
              <th>Umudugudu</th>
              <th>Isibo</th>
              <th>Ubwishingizi</th>
              <th>Mutuelle {year}</th>
              <th>Umwaka</th>
              <th>Itariki yishyuriweho</th>
              <th>Wanditse</th>
              {actionCol && <th>Igikorwa</th>}
            </tr>
          </thead>
          <tbody>
            {list.map((h) => {
              const isMit = h.ubwishingizi === "Mituweli";
              const paid = !!h.mutuelle_yishyuwe;
              return (
                <tr key={h.urugo_id}>
                  <td>
                    <div className="u-name">{h.umukuru_w_urugo || "—"}</div>
                    <div style={{ fontSize: ".78rem", color: theme.textMuted }}>
                      <i className="bi bi-people me-1" />{h.umubare_w_abagize || 0}
                    </div>
                  </td>
                  <td>{h.umudugudu || "—"}</td>
                  <td>{h.izina_ry_isibo || "—"}</td>
                  <td>{insuranceLabel(h)}</td>
                  <td><MutuelleBadge h={h} /></td>
                  <td>{isMit && paid ? (h.mutuelle_year || year) : "—"}</td>
                  <td>{isMit && paid ? fmtDate(h.mutuelle_itariki) : "—"}</td>
                  <td>{isMit && paid ? (h.mutuelle_wanditse || "—") : "—"}</td>
                  {actionCol && (
                    <td>
                      {isMit ? (
                        <button
                          type="button"
                          className={`ubw-btn ${paid ? "on" : "off"}`}
                          style={{ width: "auto", minHeight: 34, padding: "0 .8rem" }}
                          disabled={busyId === h.urugo_id}
                          onClick={() => markMutuelle(h, !paid)}
                        >
                          {busyId === h.urugo_id
                            ? <Spinner animation="border" size="sm" />
                            : paid ? "Kuraho" : "Yishyuwe"}
                        </button>
                      ) : "—"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const Section = ({ icon, title, list, showToggle }) => (
    <div className="ubw-section">
      <div className="ubw-section-head">
        <i className={`bi ${icon}`} style={{ color: theme.primary }} />
        {title}
        <Badge bg="secondary" pill className="ms-1">{list.length}</Badge>
      </div>
      <div className="ubw-section-body">
        {list.length === 0 ? (
          <div className="ubw-empty">Nta rugo ruhari muri iri tsinda.</div>
        ) : (
          <>
            <div className="ubw-cards ubw-mobileonly">
              {list.map((h) => (
                <RecordCard key={h.urugo_id} h={h} showToggle={showToggle} />
              ))}
            </div>
            <div className="ubw-desktop">
              <RecordTable list={list} showToggle={showToggle} />
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="ubw-root" style={{ background: theme.bg, minHeight: "100%", padding: "1rem" }}>
      <style>{CSS}</style>

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h4 className="ubw-title">
          <i className="bi bi-shield-check" style={{ color: theme.primary }} />
          Ubwishingizi n'ubwishyu bwa Mituweli
        </h4>
        {isUmudugudu && (
          <Badge bg="light" text="dark" style={{ border: `1px solid ${theme.border}`, fontSize: ".82rem" }}>
            <i className="bi bi-eye me-1" />Kureba gusa
          </Badge>
        )}
      </div>

      {alert.show && (
        <Alert variant={alert.variant} onClose={() => setAlert({ show: false })} dismissible>
          {alert.message}
        </Alert>
      )}

      {/* Summary tiles */}
      <div className="ubw-stats mb-3">
        {statCards.map((s) => (
          <div className="ubw-stat" style={{ "--c": s.color }} key={s.label}>
            <div className="ubw-stat-v">{s.value}</div>
            <div className="ubw-stat-l">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <Card style={{ border: `1px solid ${theme.border}`, borderRadius: "12px", marginBottom: "1.1rem" }}>
        <Card.Body className="p-2 p-md-3">
          <Row className="g-2">
            <Col xs={12} md={4}>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Shakisha urugo, umudugudu, isibo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">Ubwishingizi bwose</option>
                {INSURANCE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Select value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}>
                <option value="all">Mutuelle (byose)</option>
                <option value="yishyuwe">Yishyuwe</option>
                <option value="oya">Ntabwo yishyuwe</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={2}>
              <Form.Select value={year} onChange={(e) => setYear(Number(e.target.value))} title="Umwaka">
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: theme.primary }} />
        </div>
      ) : (
        <>
          <Section icon="bi-heart-pulse-fill" title="Mituweli" list={groups.mituweli} showToggle />
          <Section icon="bi-shield-fill-check" title="Ubundi bwishingizi bwanditse" list={groups.other} showToggle={false} />
          <Section icon="bi-three-dots" title="Ibindi" list={groups.ibindi} showToggle={false} />
        </>
      )}
    </div>
  );
};

export default Ubwishingizi;
